import * as yup from 'yup';
import { PackageOfferingEnum, PackageType } from '../../../types';

export interface PackageFormValues {
  name: string;
  offeringType: PackageOfferingEnum;
  programIndications: string[];
  productId: number | '';
}

export const DEFAULT_PACKAGE_FORM_VALUES: PackageFormValues = {
  name: '',
  offeringType: PackageOfferingEnum.ProgramIndication,
  programIndications: [],
  productId: '',
};

export interface PackageValidationContext {
  existingPackages: PackageType[];
  currentPackageId: number | null;
}

export const PackageValidationSchema = yup.object({
  name: yup.string().trim().required('Package name is required'),
  offeringType: yup
    .string()
    .oneOf(Object.values(PackageOfferingEnum))
    .required('Offering type is required'),
  programIndications: yup
    .array()
    .of(yup.string().required())
    .when('offeringType', {
      is: PackageOfferingEnum.ProgramIndication,
      then: schema =>
        schema
          .min(1, 'At least one program indication is required')
          .required()
          .test(
            'duplicate-indication-offering',
            'A package with these exact offerings already exists',
            function (value) {
              const existingPackages =
                (this.options.context as PackageValidationContext | undefined)
                  ?.existingPackages ?? [];
              const currentPackageId =
                (this.options.context as PackageValidationContext | undefined)
                  ?.currentPackageId ?? null;

              if (!value || value.length === 0) return true;

              const sorted = [...value].sort((a, b) => a.localeCompare(b));

              const conflicting = existingPackages.find(pkg => {
                if (currentPackageId !== null && pkg.id === currentPackageId) {
                  return false;
                }
                const pkgIndications = pkg.packageOfferings
                  .filter(
                    o =>
                      o.packageOffering === 'program_indication' &&
                      o.programIndication,
                  )
                  .map(o => o.programIndication as string)
                  .sort((a, b) => a.localeCompare(b));

                if (pkgIndications.length !== sorted.length) return false;
                return pkgIndications.every((ind, i) => ind === sorted[i]);
              });

              if (conflicting) {
                return this.createError({
                  message: `"${conflicting.name}" already provides these offerings`,
                });
              }
              return true;
            },
          ),
      otherwise: schema => schema.defined(),
    }),
  productId: yup.mixed<number | ''>().when('offeringType', {
    is: PackageOfferingEnum.Product,
    then: schema =>
      schema
        .test(
          'product-required',
          'Product is required',
          value => value !== '' && value !== undefined && value !== null,
        )
        .test(
          'duplicate-product-offering',
          'A package with this product already exists',
          function (value) {
            const existingPackages =
              (this.options.context as PackageValidationContext | undefined)
                ?.existingPackages ?? [];
            const currentPackageId =
              (this.options.context as PackageValidationContext | undefined)
                ?.currentPackageId ?? null;

            if (value === '' || value === undefined || value === null) {
              return true;
            }

            const conflicting = existingPackages.find(pkg => {
              if (currentPackageId !== null && pkg.id === currentPackageId) {
                return false;
              }
              return pkg.packageOfferings.some(
                o => o.packageOffering === 'product' && o.productId === value,
              );
            });

            if (conflicting) {
              return this.createError({
                message: `"${conflicting.name}" already provides this product`,
              });
            }
            return true;
          },
        ),
    otherwise: schema => schema.defined(),
  }),
}) as yup.ObjectSchema<PackageFormValues>;
